import {
  ConsentPreferences,
  CookieCategory,
  ConsentChangeEvent,
} from './types';
import { ConsentStorage } from './consentStorage';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Event listener type for consent changes
 */
type ConsentChangeListener = (event: ConsentChangeEvent) => void;

/**
 * Singleton class for managing cookie consent state
 * Provides event-driven API for consent changes
 */
export class ConsentManager {
  private static instance: ConsentManager | null = null;
  private preferences: ConsentPreferences | null = null;
  private listeners: Set<ConsentChangeListener> = new Set();
  private initialized = false;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  /**
   * Initialize consent manager (load from storage)
   * Safe to call multiple times
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.preferences = ConsentStorage.load();
    this.initialized = true;
  }

  /**
   * Check if user has given consent
   */
  hasGivenConsent(): boolean {
    return this.preferences !== null;
  }

  /**
   * Get current consent preferences
   */
  getPreferences(): ConsentPreferences | null {
    return this.preferences;
  }

  /**
   * Check if a specific category is enabled
   */
  hasConsent(category: CookieCategory): boolean {
    if (!this.preferences) {
      // No consent given - only necessary cookies allowed
      return category === 'necessary';
    }
    return this.preferences[category] === true;
  }

  /**
   * Update consent preferences
   */
  updateConsent(newPreferences: Partial<ConsentPreferences>): void {
    const oldPreferences = this.preferences;

    this.preferences = {
      ...ConsentStorage.getDefault(),
      ...this.preferences,
      ...newPreferences,
      necessary: true, // Always enforce necessary cookies
      timestamp: new Date().toISOString(),
    };

    ConsentStorage.save(this.preferences);

    // Determine which categories changed
    const changedCategories: CookieCategory[] = [];
    if (!oldPreferences) {
      // First time consent - all categories are "changed"
      changedCategories.push('necessary', 'functional', 'analytics', 'marketing');
    } else {
      (['necessary', 'functional', 'analytics', 'marketing'] as CookieCategory[]).forEach(
        (category) => {
          if (oldPreferences[category] !== this.preferences![category]) {
            changedCategories.push(category);
          }
        }
      );
    }

    // Notify listeners
    this.notifyListeners({
      preferences: this.preferences,
      changedCategories,
    });
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.updateConsent(ConsentStorage.getAcceptAll());
  }

  /**
   * Reject all optional cookies (keep only necessary)
   */
  rejectAll(): void {
    this.updateConsent(ConsentStorage.getRejectAll());
  }

  /**
   * Reset consent (clear all preferences)
   */
  resetConsent(): void {
    const oldPreferences = this.preferences;
    this.preferences = null;
    ConsentStorage.clear();

    // If there was consent before, notify listeners
    if (oldPreferences) {
      this.notifyListeners({
        preferences: ConsentStorage.getDefault(),
        changedCategories: ['necessary', 'functional', 'analytics', 'marketing'],
      });
    }
  }

  /**
   * Subscribe to consent changes
   * Returns unsubscribe function
   */
  onChange(listener: ConsentChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of consent change
   */
  private notifyListeners(event: ConsentChangeEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        logger.error('Error in consent change listener:', error);
      }
    });
  }

  /**
   * Check if functional cookies are allowed (for backward compatibility)
   */
  canUseFunctionalCookies(): boolean {
    return this.hasConsent('functional');
  }

  /**
   * Check if analytics cookies are allowed (for future use)
   */
  canUseAnalytics(): boolean {
    return this.hasConsent('analytics');
  }

  /**
   * Check if marketing cookies are allowed (for future use)
   */
  canUseMarketing(): boolean {
    return this.hasConsent('marketing');
  }
}

/**
 * Export singleton instance for convenience
 */
const consentManager = ConsentManager.getInstance();
